const GUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function hasWindow() {
  return typeof window !== 'undefined';
}

export function isPerformanceTimingEnabled() {
  if (import.meta.env.DEV) return true;
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem('sentinel.apiTiming') === '1';
  } catch {
    return false;
  }
}

export function sanitizeTimingPath(path) {
  if (!path || typeof path !== 'string') return '';
  try {
    const url = new URL(path, hasWindow() ? window.location.origin : 'https://portal.local');
    const query = [];
    for (const [key, value] of url.searchParams.entries()) {
      if (['category', 'pageSize', 'limit', 'activeOnly', 'includeSuppressed', 'framework'].includes(key)) {
        query.push(`${key}=${encodeURIComponent(value).slice(0, 80)}`);
      } else {
        query.push(key);
      }
    }
    const cleanedPath = url.pathname.replace(GUID_PATTERN, '{guid}');
    return query.length > 0 ? `${cleanedPath}?${query.join('&')}` : cleanedPath;
  } catch {
    return path.replace(GUID_PATTERN, '{guid}').split('&token=')[0].slice(0, 240);
  }
}

function emitTimingEvent(name, detail) {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function timingState() {
  if (!hasWindow()) return null;
  window.__sentinelPerf = window.__sentinelPerf || {
    apiInflight: 0,
    apiEvents: [],
    routeEvents: [],
  };
  return window.__sentinelPerf;
}

export function beginApiTiming(method, path) {
  const state = timingState();
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (state) state.apiInflight += 1;
  return { method, path, sanitizedPath: sanitizeTimingPath(path), startedAt };
}

export function endApiTiming(token, { ok, status = null, error = null } = {}) {
  const elapsedMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - token.startedAt);
  const event = {
    type: 'api',
    method: token.method,
    path: token.sanitizedPath,
    ok,
    status,
    elapsedMs,
    error: error?.name || error?.code || null,
    at: new Date().toISOString(),
  };
  const state = timingState();
  if (state) {
    state.apiInflight = Math.max(0, state.apiInflight - 1);
    state.apiEvents.push(event);
    state.apiEvents = state.apiEvents.slice(-300);
  }
  emitTimingEvent('sentinel:apiTiming', event);
  if (isPerformanceTimingEnabled()) {
    const message = `[api timing] ${event.method} ${event.path} ${ok ? 'completed' : 'failed'} in ${elapsedMs}ms`;
    (ok ? console.info : console.warn)(message, event);
  }
  return event;
}

export function markRouteTiming(pathname, phase, extra = {}) {
  const event = {
    type: 'route',
    pathname,
    phase,
    elapsedMs: extra.elapsedMs ?? null,
    apiInflight: timingState()?.apiInflight ?? 0,
    at: new Date().toISOString(),
    ...extra,
  };
  const state = timingState();
  if (state) {
    state.routeEvents.push(event);
    state.routeEvents = state.routeEvents.slice(-300);
  }
  emitTimingEvent('sentinel:routeTiming', event);
  if (isPerformanceTimingEnabled()) {
    const elapsed = event.elapsedMs == null ? '' : ` in ${event.elapsedMs}ms`;
    console.info(`[route timing] ${pathname} ${phase}${elapsed}`, event);
  }
  return event;
}

export function currentApiInflight() {
  return timingState()?.apiInflight ?? 0;
}
